import { useEffect, useRef, useState } from "react";
import axios from "axios";
import moment from "moment";
import * as XLSX from "xlsx-js-style";
import { Html5Qrcode } from "html5-qrcode";

const listRit = [1, 2, 3, 4, 5].map((ele) => ({
  value: ele,
  label: `Rit ${ele}`,
}));

const formatMenit = (menit) => {
  if (menit === null || menit === undefined) return "-";
  return `${Math.floor(menit / 60)}j ${menit % 60}m`;
};

const formatJam = (tanggal) =>
  tanggal ? moment(tanggal).utcOffset("+0700").format("HH:mm") : "-";

const formatWaktu = (tanggal) =>
  tanggal
    ? moment(tanggal).utcOffset("+0700").format("DD MMM YYYY, HH:mm:ss") +
      " WIB"
    : "-";

export const useInOutKendaraan = () => {
  const dataAuth = localStorage.getItem("auth");
  const dataUser = JSON.parse(dataAuth);

  const role = String(dataUser.role || "").toLowerCase();

  const canShowQr = [
    "security",
    "super_user",
    "warehouse_admin",
    "warehouse_gbj_admin",
  ].includes(role);
  const canScan = role !== "security";

  const headers = { Authorization: `Bearer ${dataUser.auth}` };

  const [subMenu, setSubMenu] = useState(canShowQr ? "QR" : "Scan");

  // ===== POPUP =====
  const [popup, setPopup] = useState({
    open: false,
    title: "",
    message: "",
    type: "info",
  });

  const showPopup = (message, type = "info", title = "") => {
    setPopup({ open: true, title: title, message: message, type: type });
  };

  const closePopup = () => {
    setPopup((prev) => ({ ...prev, open: false }));
  };

  // ===== SHOW QR =====
  const [dataQr, setDataQr] = useState();
  const [sisaDetik, setSisaDetik] = useState(0);

  const getQrToken = async () => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_ENDPOINT}/getQrToken`,
        {},
        { headers }
      );
      setDataQr(res.data);
    } catch (error) {
      console.log(error);
      setDataQr({ message: "Gagal mengambil QR" });
    }
  };

  useEffect(() => {
    if (subMenu !== "QR" || !canShowQr) return;

    getQrToken();
    const intervalToken = setInterval(getQrToken, 5000);
    const intervalHitung = setInterval(() => {
      setSisaDetik((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => {
      clearInterval(intervalToken);
      clearInterval(intervalHitung);
      setDataQr();
    };
  }, [subMenu]);

  useEffect(() => {
    if (dataQr?.expiresIn !== undefined) setSisaDetik(dataQr.expiresIn);
  }, [dataQr]);

  // ===== ANTI SCREENSHOT (best effort) =====
  const [qrHidden, setQrHidden] = useState(false);

  useEffect(() => {
    if (subMenu !== "QR" || !canShowQr) return;

    let timer;

    const hide = () => setQrHidden(true);

    const showIfFocused = () => {
      if (document.visibilityState === "visible" && document.hasFocus()) {
        setQrHidden(false);
      }
    };

    const clearClipboard = () => {
      try {
        navigator.clipboard?.writeText("");
      } catch (err) {}
    };

    const onKeyDown = (e) => {
      const k = (e.key || "").toLowerCase();
      const isShot =
        e.key === "PrintScreen" ||
        ((e.ctrlKey || e.metaKey) && (k === "p" || k === "s")) ||
        // Mac: Cmd+Shift+3/4/5, Windows: Win+Shift+S
        (e.metaKey &&
          e.shiftKey &&
          ["Digit3", "Digit4", "Digit5", "KeyS"].includes(e.code));

      if (isShot) {
        e.preventDefault();
        hide();
        clearClipboard();
        clearTimeout(timer);
        timer = setTimeout(showIfFocused, 2000);
      }
    };

    const onKeyUp = (e) => {
      if (e.key === "PrintScreen") {
        hide();
        clearClipboard();
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "hidden") hide();
      else showIfFocused();
    };

    const onContextMenu = (e) => e.preventDefault();

    if (!document.hasFocus()) hide();

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", hide);
    window.addEventListener("focus", showIfFocused);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("beforeprint", hide);
    window.addEventListener("afterprint", showIfFocused);
    document.addEventListener("contextmenu", onContextMenu);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", hide);
      window.removeEventListener("focus", showIfFocused);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("beforeprint", hide);
      window.removeEventListener("afterprint", showIfFocused);
      document.removeEventListener("contextmenu", onContextMenu);
      setQrHidden(false);
    };
  }, [subMenu]);

  // ===== SCAN =====
  const [type, setType] = useState("OUT"); // IN | OUT
  const [listKendaraan, setListKendaraan] = useState([]);
  const [listCustomer, setListCustomer] = useState([]);
  const [vehiclePick, setVehiclePick] = useState(null);
  const [customerPick, setCustomerPick] = useState([]); // array (multi customer)
  const [ritPick, setRitPick] = useState(null);
  const [keterangan, setKeterangan] = useState("");
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef(null);
  const sudahScanRef = useRef(false);

  const handleKeterangan = (value) => {
    setKeterangan(value.toUpperCase());
  };

  useEffect(() => {
    if (subMenu !== "Scan" || !canScan) return;
    const getList = async () => {
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_ENDPOINT}/getListKendaraanCustomer`,
          {},
          { headers }
        );
        setListKendaraan(res.data.listKendaraan || []);
        setListCustomer(res.data.listCustomer || []);
      } catch (error) {
        console.log(error);
      }
    };
    getList();
  }, [subMenu]);

  const stopScan = async () => {
    try {
      if (scannerRef.current?.isScanning) {
        await scannerRef.current.stop();
      }
    } catch (error) {
      console.log(error);
    }
    setScanning(false);
  };

  const kirimScan = async (token) => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_ENDPOINT}/scanQr`,
        {
          token: token,
          vehicleId: vehiclePick.value,
          customers: customerPick.map((c) => c.value),
          rit: ritPick?.value || null,
          keterangan: type === "IN" ? keterangan.trim() : "",
          type: type,
        },
        { headers }
      );

      if (res.data.code === 200) {
        showPopup(
          res.data.message,
          res.data.status === "TERLAMBAT" ? "error" : "success",
          type === "IN" ? "Scan Masuk" : "Scan Keluar"
        );
        setVehiclePick(null);
        setCustomerPick([]);
        setRitPick(null);
        setKeterangan("");
      } else {
        showPopup(res.data.message, "error", "Scan Gagal");
      }
    } catch (error) {
      console.log(error);
      showPopup("Gagal mengirim scan", "error", "Scan Gagal");
    }
  };

  const startScan = async () => {
    if (!vehiclePick)
      return showPopup("Pilih kendaraan terlebih dahulu", "error");
    if (type === "OUT" && customerPick.length === 0)
      return showPopup("Pilih customer terlebih dahulu", "error");
    if (type === "OUT" && !ritPick)
      return showPopup("Pilih rit terlebih dahulu", "error");

    sudahScanRef.current = false;
    setScanning(true);

    setTimeout(async () => {
      try {
        scannerRef.current = new Html5Qrcode("reader");
        await scannerRef.current.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: 250 },
          async (decodedText) => {
            if (sudahScanRef.current) return;
            sudahScanRef.current = true;
            await stopScan();
            kirimScan(decodedText);
          },
          () => {}
        );
      } catch (error) {
        console.log(error);
        setScanning(false);
        showPopup("Kamera tidak bisa diakses", "error");
      }
    }, 100);
  };

  useEffect(() => {
    if (subMenu !== "Scan") stopScan();
  }, [subMenu]);

  useEffect(() => {
    return () => {
      if (scannerRef.current?.isScanning) scannerRef.current.stop();
    };
  }, []);

  // ===== MONITORING (per hari) =====
  const [tanggal, setTanggal] = useState(
    moment().utcOffset("+0700").format("YYYY-MM-DD")
  );
  const [dataLog, setDataLog] = useState([]);
  const [serverNow, setServerNow] = useState(null);
  const [detailRow, setDetailRow] = useState(null);

  const hariIni = moment().utcOffset("+0700").format("YYYY-MM-DD");

  // geser tanggal: -1 = kemarin, +1 = besok
  const geserHari = (n) => {
    setTanggal(moment(tanggal).add(n, "days").format("YYYY-MM-DD"));
  };

  useEffect(() => {
    if (subMenu !== "Monitoring") return;

    const getLog = async () => {
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_ENDPOINT}/getLogInOutKendaraan`,
          { tanggal: tanggal },
          { headers }
        );
        if (res.data.code === 200) {
          setDataLog(res.data.data || []);
          setServerNow(res.data.serverNow);
        }
      } catch (error) {
        console.log(error);
      }
    };

    getLog();
    // refresh otomatis tiap 1 menit selama tab Monitoring terbuka
    const interval = setInterval(getLog, 60000);
    return () => clearInterval(interval);
  }, [subMenu, tanggal]);

  // status tiap baris: merah jika terlambat atau masih di luar & lewat batas
  const getStatusRow = (row) => {
    if (row.timeIn) {
      if (row.status === "TERLAMBAT") {
        return {
          label: `TERLAMBAT +${row.terlambatMenit} MNT`,
          late: true,
          out: false,
        };
      }
      return { label: "TEPAT WAKTU", late: false, out: false };
    }
    const lewat = serverNow
      ? Math.round(
          (new Date(serverNow).getTime() -
            new Date(row.batasKembali).getTime()) /
            60000
        )
      : 0;
    if (lewat > 0) {
      return {
        label: `DI LUAR, LEWAT BATAS ${lewat} MNT`,
        late: true,
        out: true,
      };
    }
    return { label: "DI LUAR", late: false, out: true };
  };

  const rowsMonitoring = dataLog.map((row) => ({
    ...row,
    statusRow: getStatusRow(row),
  }));

  const ringkasan = {
    total: rowsMonitoring.length,
    tepat: rowsMonitoring.filter(
      (ele) => !ele.statusRow.late && !ele.statusRow.out
    ).length,
    terlambat: rowsMonitoring.filter((ele) => ele.statusRow.late).length,
    diLuar: rowsMonitoring.filter((ele) => ele.statusRow.out).length,
  };

  const exportExcel = () => {
    if (rowsMonitoring.length === 0) {
      return showPopup("Tidak ada data untuk diexport", "info");
    }

    const fmtTanggal = (t) =>
      t ? moment(t).utcOffset("+0700").format("DD/MM/YYYY") : "-";
    const fmtJam = (t) =>
      t ? moment(t).utcOffset("+0700").format("HH:mm") : "-";
    const fmtLengkap = (t) =>
      t ? moment(t).utcOffset("+0700").format("DD/MM/YYYY HH:mm") : "-";

    const data = rowsMonitoring.map((row, i) => ({
      "No.": i + 1,
      Nopol: row.nopol,
      Customer: row.customer,
      Rit: row.rit,
      "Tgl Keluar": fmtTanggal(row.timeOut),
      "Jam Keluar": fmtJam(row.timeOut),
      "Tgl Masuk": fmtTanggal(row.timeIn),
      "Jam Masuk": fmtJam(row.timeIn),
      "Batas Kembali": fmtLengkap(row.batasKembali),
      "Durasi Standar": formatMenit(row.durasiStandarMenit),
      "Toleransi (mnt)": row.toleransiMenit,
      "Durasi Aktual": formatMenit(row.durasiAktualMenit),
      Status: row.statusRow.label,
      "Terlambat (mnt)": row.terlambatMenit || 0,
      "Security Keluar": row.securityOut || "-",
      "Security Masuk": row.securityIn || "-",
      "User Keluar": row.userOut || "-",
      "User Masuk": row.userIn || "-",
      Keterangan: row.keterangan || "-",
    }));

    const ws = XLSX.utils.json_to_sheet(data);

    ws["!cols"] = [
      { wch: 3 }, // No.
      { wch: 9 }, // Nopol
      { wch: 22 }, // Customer (bisa lebih dari satu)
      { wch: 4 }, // Rit
      { wch: 10 }, // Tgl Keluar
      { wch: 9 }, // Jam Keluar
      { wch: 10 }, // Tgl Masuk
      { wch: 9 }, // Jam Masuk
      { wch: 15 }, // Batas Kembali
      { wch: 12 }, // Durasi Standar
      { wch: 12 }, // Toleransi (mnt)
      { wch: 11 }, // Durasi Aktual
      { wch: 20 }, // Status
      { wch: 13 }, // Terlambat (mnt)
      { wch: 16 }, // Security Keluar
      { wch: 16 }, // Security Masuk
      { wch: 10 }, // User Keluar
      { wch: 10 }, // User Masuk
      { wch: 30 }, // Keterangan
    ];

    const border = {
      top: { style: "thin", color: { rgb: "000000" } },
      bottom: { style: "thin", color: { rgb: "000000" } },
      left: { style: "thin", color: { rgb: "000000" } },
      right: { style: "thin", color: { rgb: "000000" } },
    };

    const jumlahKolom = Object.keys(data[0]).length;

    // header
    for (let c = 0; c < jumlahKolom; c++) {
      const cell = ws[XLSX.utils.encode_cell({ r: 0, c })];
      cell.s = {
        font: { bold: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "1F4E78" } },
        alignment: { horizontal: "center", vertical: "center" },
        border,
      };
    }

    const kolomTengah = [0, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 13];

    rowsMonitoring.forEach((row, i) => {
      for (let c = 0; c < jumlahKolom; c++) {
        const cell = ws[XLSX.utils.encode_cell({ r: i + 1, c })];
        cell.s = {
          font: row.statusRow.late ? { color: { rgb: "C00000" } } : {},
          alignment: kolomTengah.includes(c)
            ? { horizontal: "center", vertical: "center" }
            : { vertical: "center" },
          border,
        };
      }
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Monitoring");
    XLSX.writeFile(wb, `Monitoring_Kendaraan_${tanggal}.xlsx`);
  };
  return {
    dataUser,
    canShowQr,
    canScan,
    subMenu,
    setSubMenu,
    dataQr,
    sisaDetik,
    qrHidden,
    type,
    setType,
    listKendaraan,
    listCustomer,
    listRit,
    vehiclePick,
    setVehiclePick,
    customerPick,
    setCustomerPick,
    ritPick,
    setRitPick,
    keterangan,
    handleKeterangan,
    scanning,
    startScan,
    stopScan,
    popup,
    closePopup,
    tanggal,
    setTanggal,
    geserHari,
    hariIni,
    rowsMonitoring,
    ringkasan,
    formatMenit,
    formatJam,
    formatWaktu,
    detailRow,
    setDetailRow,
    exportExcel,
  };
};
